import React, { useEffect, useRef, useState } from "react";
import { HashRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router";
import { format } from "date-fns";
import { AppProvider, useAppState, useAppActions } from "./contexts/AppContext";
import { FieldConfigProvider } from "./contexts/FieldConfigContext";
import { PresetsProvider } from "./contexts/PresetsContext";
import { LocationsProvider, useLocations } from "./contexts/LocationsContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { BasePathProvider, useBasePath } from "./contexts/BasePathContext";
import { DemoProvider, useDemo } from "./contexts/DemoContext";
import { LicenseProvider, useFeatureAccess } from "./contexts/LicenseContext";
import { usePlatform } from "./contexts/PlatformContext";
import { useKeyboardShortcuts } from "./hooks/useKeyboardShortcuts";
import { useSelection } from "./hooks/useSelection";
import { useUndoRedo } from "./hooks/useUndoRedo";
import { useFiles } from "./hooks/useFiles";
import { useIsMobile } from "./hooks/useMobile";
import { usePageTitle } from "./hooks/usePageTitle";
import { Toaster, toast } from "./components/ui";
import Header from "./components/Header/Header";
import Editor from "./components/Editor/Editor";
import CalendarView from "./components/Calendar/CalendarView";
import TimelineV3View from "./components/Timeline/timeline-v3/TimelineV3View";
import TableView from "./components/TableView/TableView";
import Preferences from "./components/Preferences/Preferences";
import UploadScreen from "./components/UploadScreen/UploadScreen";
import PresetCommandModal from "./components/common/PresetCommandModal";
import LocationPickerModal from "./components/common/LocationPickerModal";
import KeyboardShortcutsDialog from "./components/common/KeyboardShortcutsDialog";
import DateTimeShortcutDialog from "./components/common/DateTimeShortcutDialog";
import MobileWorkspaceSwitcher from "./components/MobileWorkspaceSwitcher";
import { ProWall } from "./components/ProWall";
import { getWorkspacePageTitle } from "./utils/pageTitle";
import { quantizeGpsPairToNearestMeter } from "./utils/gpsPrecision";

// Helper to derive viewMode from a stripped pathname (no basePath prefix)
const getPathViewMode = (strippedPathname) => {
  if (strippedPathname.startsWith("/settings")) return "preferences";
  if (strippedPathname === "/table") return "table";
  if (strippedPathname === "/calendar") return "calendar";
  if (strippedPathname === "/timeline-v3") return "timelineV3";
  return "editor";
};

// Extracted component - defined outside AppContent to prevent unmount/remount on re-render
// This was the root cause of losing cell focus when files changed
function AppWithWorkspaceChrome({
  showUploadScreen,
  isMobile,
  viewMode,
  calendarSelectedDate,
  setCalendarSelectedDate,
  setCalendarSelectedHour,
  setCalendarPanelExpanded,
  editorMobileLeftOpen,
  setEditorMobileLeftOpen,
  editorMobileRightOpen,
  setEditorMobileRightOpen,
  calendarMobileLeftOpen,
  setCalendarMobileLeftOpen,
  calendarMobileRightOpen,
  setCalendarMobileRightOpen,
  handleApplyPreset,
  handleApplyLocation,
  handleSaveLocation,
  presetModalOpen,
  setPresetModalOpen,
  locationModalOpen,
  setLocationModalOpen,
  keyboardShortcutsModalOpen,
  setKeyboardShortcutsModalOpen,
  keyboardShortcutsInitialView,
  dateTimeShortcutModalOpen,
  setDateTimeShortcutModalOpen,
  dateTimeShortcutTargetIndices,
  handleApplyShortcutDateTime,
  selectedIndices,
  selectedCellRange,
  files,
}) {
  const navigate = useNavigate();
  const { appPath } = useBasePath();
  const [mobileHeaderVisible, setMobileHeaderVisible] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const mobileHeaderVisibleRef = useRef(true);
  const scrollPositionsRef = useRef(new WeakMap());
  const shouldUseMobileChrome = isMobile && !showUploadScreen;
  const showMobileWorkspaceSwitcher =
    shouldUseMobileChrome &&
    files.length > 0 &&
    (viewMode === "editor" || viewMode === "calendar");
  const showMobilePaneControls = showMobileWorkspaceSwitcher;

  const handleMobileViewChange = (nextView) => {
    setEditorMobileLeftOpen(false);
    setEditorMobileRightOpen(false);
    setCalendarMobileLeftOpen(false);
    setCalendarMobileRightOpen(false);
    navigate(appPath(`/${nextView}`));
  };

  const handleMobileLeftPaneToggle = () => {
    if (viewMode === "editor") {
      const nextOpen = !editorMobileLeftOpen;
      setEditorMobileRightOpen(false);
      setEditorMobileLeftOpen(nextOpen);
      return;
    }

    if (viewMode === "calendar") {
      const nextOpen = !calendarMobileLeftOpen;
      setCalendarMobileRightOpen(false);
      setCalendarMobileLeftOpen(nextOpen);
    }
  };

  const handleMobileRightPaneToggle = () => {
    if (viewMode === "editor") {
      const nextOpen = !editorMobileRightOpen;
      setEditorMobileLeftOpen(false);
      setEditorMobileRightOpen(nextOpen);
      return;
    }

    if (viewMode === "calendar") {
      const nextOpen = !calendarMobileRightOpen;
      setCalendarMobileLeftOpen(false);
      if (nextOpen && !calendarSelectedDate) {
        setCalendarSelectedDate(format(new Date(), "yyyy-MM-dd"));
        setCalendarSelectedHour(null);
      }
      setCalendarPanelExpanded(nextOpen);
      setCalendarMobileRightOpen(nextOpen);
    }
  };

  const mobilePaneControls = showMobilePaneControls
    ? {
        leftPaneOpen:
          viewMode === "editor" ? editorMobileLeftOpen : calendarMobileLeftOpen,
        rightPaneOpen:
          viewMode === "editor"
            ? editorMobileRightOpen
            : calendarMobileRightOpen,
        leftPaneLabel: "queue",
        rightPaneLabel: viewMode === "editor" ? "assistant" : "details",
        onToggleLeftPane: handleMobileLeftPaneToggle,
        onToggleRightPane: handleMobileRightPaneToggle,
      }
    : null;

  const setHeaderVisibility = (visible) => {
    if (mobileHeaderVisibleRef.current === visible) return;
    mobileHeaderVisibleRef.current = visible;
    setMobileHeaderVisible(visible);
  };

  useEffect(() => {
    setHeaderVisibility(true);
    setMobileMenuOpen(false);
    scrollPositionsRef.current = new WeakMap();
  }, [viewMode, showUploadScreen]);

  useEffect(() => {
    if (!shouldUseMobileChrome) {
      setHeaderVisibility(true);
      return;
    }

    if (mobileMenuOpen) {
      setHeaderVisibility(true);
      return;
    }

    const handleScroll = (event) => {
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;
      if (target.closest('[data-slot="sheet-content"]')) return;

      const currentTop = target.scrollTop;
      if (typeof currentTop !== "number") return;

      const previousTop = scrollPositionsRef.current.get(target) ?? currentTop;
      scrollPositionsRef.current.set(target, currentTop);

      const delta = currentTop - previousTop;
      if (Math.abs(delta) < 10) return;

      if (currentTop <= 12) {
        setHeaderVisibility(true);
        return;
      }

      if (delta > 0) {
        setHeaderVisibility(false);
      } else {
        setHeaderVisibility(true);
      }
    };

    window.addEventListener("scroll", handleScroll, true);
    return () => window.removeEventListener("scroll", handleScroll, true);
  }, [shouldUseMobileChrome, mobileMenuOpen]);

  const mobileHeaderClassName =
    shouldUseMobileChrome && !mobileHeaderVisible
      ? "max-h-0 opacity-0"
      : isMobile && mobileMenuOpen
        ? "max-h-[75vh] opacity-100"
        : "max-h-[80px] opacity-100";

  return (
    <>
      {showUploadScreen ? (
        <div className="relative flex flex-col h-screen bg-linear-to-br from-bg-deep to-bg-primary overflow-hidden">
          <div className="relative z-10 flex h-full flex-col">
            <div
              className={`shrink-0 overflow-hidden transition-[max-height,opacity] duration-200 ease-out ${mobileHeaderClassName}`}
            >
              <Header
                mobilePaneControls={mobilePaneControls}
                mobileMenuOpen={mobileMenuOpen}
                onMobileMenuOpenChange={setMobileMenuOpen}
              />
            </div>
            <UploadScreen />
          </div>
        </div>
      ) : (
        <div className="relative flex flex-col h-screen bg-linear-to-br from-bg-deep to-bg-primary overflow-hidden">
          <div className="relative z-10 flex h-full flex-col">
            <div
              className={`shrink-0 overflow-hidden transition-[max-height,opacity] duration-200 ease-out ${mobileHeaderClassName}`}
            >
              <Header
                mobilePaneControls={mobilePaneControls}
                mobileMenuOpen={mobileMenuOpen}
                onMobileMenuOpenChange={setMobileMenuOpen}
              />
            </div>
            <div
              className={`flex flex-1 overflow-hidden ${isMobile ? "px-safe" : ""} ${showMobileWorkspaceSwitcher ? "pb-[calc(3.875rem+env(safe-area-inset-bottom))]" : ""}`}
            >
              <Routes>
                <Route path="/" element={<Navigate to="editor" replace />} />
                <Route path="editor" element={<Editor />} />
                <Route path="table" element={<TableView />} />
                <Route path="calendar" element={<CalendarView />} />
                <Route path="timeline" element={<Navigate to="timeline-v3" replace />} />
                <Route path="timeline-v3" element={<TimelineV3View />} />
                <Route path="settings" element={<Navigate to="general" replace />} />
                <Route path="settings/:tab/:action" element={<Preferences />} />
                <Route path="settings/:tab/:action/:itemId" element={<Preferences />} />
                <Route path="settings/:tab" element={<Preferences />} />
              </Routes>
            </div>

            {showMobileWorkspaceSwitcher && (
              <MobileWorkspaceSwitcher
                activeView={viewMode}
                onViewChange={handleMobileViewChange}
              />
            )}

            {/* Global Modals */}
            <PresetCommandModal
              open={presetModalOpen}
              onOpenChange={setPresetModalOpen}
              selectedIndices={selectedIndices}
              selectedCellRange={selectedCellRange}
              files={files}
              onApplyPreset={handleApplyPreset}
            />
            <LocationPickerModal
              open={locationModalOpen}
              onOpenChange={setLocationModalOpen}
              selectedIndices={selectedIndices}
              selectedCellRange={selectedCellRange}
              files={files}
              onApplyLocation={handleApplyLocation}
              onSaveLocation={handleSaveLocation}
            />
            <KeyboardShortcutsDialog
              open={keyboardShortcutsModalOpen}
              onOpenChange={setKeyboardShortcutsModalOpen}
              initialView={keyboardShortcutsInitialView}
            />
            <DateTimeShortcutDialog
              open={dateTimeShortcutModalOpen}
              onOpenChange={setDateTimeShortcutModalOpen}
              files={files}
              targetIndices={dateTimeShortcutTargetIndices}
              onApplyDateTime={handleApplyShortcutDateTime}
            />
          </div>
        </div>
      )}
    </>
  );
}

function AppContent({ initialFiles = null }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { appPath, stripBase } = useBasePath();
  const strippedPathname = stripBase(location.pathname);
  const viewMode = getPathViewMode(strippedPathname);
  usePageTitle(getWorkspacePageTitle(viewMode));
  const {
    files,
    calendarSelectedDate,
    selectedCellRange,
    tableVisibleRowMap,
    locationModalOpen,
    presetModalOpen,
    keyboardShortcutsModalOpen,
    keyboardShortcutsInitialView,
    editorMobileLeftOpen,
    editorMobileRightOpen,
    calendarMobileLeftOpen,
    calendarMobileRightOpen,
  } = useAppState();
  const {
    setFiles,
    setLocationModalOpen,
    setPresetModalOpen,
    setKeyboardShortcutsModalOpen,
    setKeyboardShortcutsInitialView,
    setCalendarSelectedDate,
    setCalendarSelectedHour,
    setCalendarPanelExpanded,
    setEditorMobileLeftOpen,
    setEditorMobileRightOpen,
    setCalendarMobileLeftOpen,
    setCalendarMobileRightOpen,
    setTableVisibleRowMap,
  } = useAppActions();
  const { selectedIndices, primaryIndex } = useSelection();
  const { recordAction } = useUndoRedo();
  const { addLocation } = useLocations();
  const { addFiles, assignFilesDate } = useFiles();
  const { isDemo } = useDemo();
  const { allowed: canUsePresets } = useFeatureAccess("presets");
  const isMobile = useIsMobile();
  const hasLoadedInitialFiles = useRef(false);
  const hasLoadedPreloadedFile = useRef(false);
  const [dateTimeShortcutModalOpen, setDateTimeShortcutModalOpen] =
    useState(false);

  useKeyboardShortcuts({
    onOpenPresetModal: () => {
      setPresetModalOpen(true);
    },
    onOpenLocationModal: () => setLocationModalOpen(true),
    onOpenKeyboardShortcuts: () => {
      const shortcutsView =
        viewMode === "editor" ||
        viewMode === "table" ||
        viewMode === "calendar" ||
        viewMode === "timelineV3"
          ? viewMode
          : "editor";
      setKeyboardShortcutsInitialView(shortcutsView);
      setKeyboardShortcutsModalOpen(true);
    },
    onOpenSetDateTime: () => {
      setDateTimeShortcutModalOpen(true);
    },
  });

  // Redirect mobile users from /table to /editor (Table view not supported on mobile)
  useEffect(() => {
    if (isMobile && viewMode === "table") {
      navigate(appPath("/editor"));
    }
  }, [isMobile, viewMode, navigate, appPath]);

  useEffect(() => {
    if (
      isMobile &&
      strippedPathname === "/timeline-v3"
    ) {
      navigate(appPath("/calendar"), { replace: true });
    }
  }, [isMobile, strippedPathname, navigate, appPath]);

  useEffect(() => {
    if (viewMode !== "editor") {
      setEditorMobileLeftOpen(false);
      setEditorMobileRightOpen(false);
    }
    if (viewMode !== "calendar") {
      setCalendarMobileLeftOpen(false);
      setCalendarMobileRightOpen(false);
      setCalendarPanelExpanded(false);
    }
  }, [
    viewMode,
    setEditorMobileLeftOpen,
    setEditorMobileRightOpen,
    setCalendarMobileLeftOpen,
    setCalendarMobileRightOpen,
    setCalendarPanelExpanded,
  ]);

  // Load initial files on mount (for demo mode)
  useEffect(() => {
    if (initialFiles && !hasLoadedInitialFiles.current) {
      addFiles(initialFiles);
      hasLoadedInitialFiles.current = true;
    }
  }, [initialFiles, addFiles]);

  // Load preloaded file from router state (landing page drop zone)
  useEffect(() => {
    const preloadedFile = location.state?.preloadedFile;
    if (preloadedFile && !hasLoadedPreloadedFile.current) {
      const { file } = preloadedFile;

      // Create an array-like object for addFiles
      const mockFileList = [file];
      addFiles(mockFileList);
      hasLoadedPreloadedFile.current = true;

      // Clear router state by navigating to editor (prevents re-loading on back navigation)
      navigate(appPath("/editor"), { replace: true, state: null });
    }
  }, [location.state, appPath, navigate, addFiles]);

  // Warn before leaving if there are unsaved changes (skip in demo mode)
  useEffect(() => {
    // Skip warning in demo mode
    if (isDemo) return;

    const handleBeforeUnload = (e) => {
      const hasUnsavedChanges = files.some((f) => f.hasChanges);
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = ""; // Chrome requires returnValue to be set
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [files, isDemo]);

  // Handle applying preset to selected files
  const getSelectedFileIndicesFromCells = () => {
    if (!selectedCellRange) return [];

    const indices = [];
    for (let row = selectedCellRange.minRow; row <= selectedCellRange.maxRow; row++) {
      const mappedIndex = tableVisibleRowMap[row];
      if (Number.isInteger(mappedIndex)) {
        indices.push(mappedIndex);
      } else if (row >= 0 && row < files.length) {
        indices.push(row);
      }
    }

    return Array.from(new Set(indices));
  };

  const getShortcutTargetIndices = () => {
    if (selectedCellRange) {
      return getSelectedFileIndicesFromCells();
    }

    if (selectedIndices.size > 0) {
      return Array.from(selectedIndices);
    }

    return Number.isInteger(primaryIndex) && primaryIndex >= 0
      ? [primaryIndex]
      : [];
  };

  const dateTimeShortcutTargetIndices = getShortcutTargetIndices();

  const handleApplyShortcutDateTime = (date) => {
    const targetIndices = getShortcutTargetIndices();
    if (targetIndices.length === 0) return;
    assignFilesDate(targetIndices, date);
  };

  // Handle applying preset to selected files
  const handleApplyPreset = (preset) => {
    if (!canUsePresets) {
      toast.info("Presets are a Pro feature. Unlock Pro in Settings > License.");
      return;
    }

    // Derive row indices to apply preset to:
    // - If cells are selected, use rows from the cell selection range
    // - Otherwise, use the selected row indices
    let targetIndices;
    if (selectedCellRange) {
      targetIndices = getSelectedFileIndicesFromCells();
    } else {
      targetIndices = Array.from(selectedIndices);
    }
    if (targetIndices.length === 0) return;

    const targetIndicesSet = new Set(targetIndices);

    // Store previous state for undo
    const previousData = files.map((f) => ({
      modifiedExifData: { ...f.modifiedExifData },
      hasChanges: f.hasChanges,
    }));

    // Apply preset to selected files
    setFiles((prev) =>
      prev.map((f, i) => {
        if (targetIndicesSet.has(i)) {
          const newData = { ...f.modifiedExifData };
          preset.includedFields.forEach((fieldId) => {
            if (preset.values[fieldId] !== undefined) {
              newData[fieldId] = preset.values[fieldId];
            }
          });
          return {
            ...f,
            modifiedExifData: newData,
            hasChanges: true,
          };
        }
        return f;
      }),
    );

    // Record for undo/redo
    recordAction(
      `Apply preset "${preset.name}" to ${targetIndices.length} files`,
      // Undo
      () => {
        setFiles((prev) =>
          prev.map((f, i) => ({
            ...f,
            modifiedExifData: previousData[i].modifiedExifData,
            hasChanges: previousData[i].hasChanges,
          })),
        );
      },
      // Redo
      () => {
        setFiles((prev) =>
          prev.map((f, i) => {
            if (targetIndicesSet.has(i)) {
              const newData = { ...f.modifiedExifData };
              preset.includedFields.forEach((fieldId) => {
                if (preset.values[fieldId] !== undefined) {
                  newData[fieldId] = preset.values[fieldId];
                }
              });
              return { ...f, modifiedExifData: newData, hasChanges: true };
            }
            return f;
          }),
        );
      },
      targetIndices,
    );
  };

  // Handle applying location to selected files
  const handleApplyLocation = (location) => {
    // Derive row indices: cell range first, then row selection
    let targetIndices;
    if (selectedCellRange) {
      targetIndices = getSelectedFileIndicesFromCells();
    } else {
      targetIndices = Array.from(selectedIndices);
    }
    if (targetIndices.length === 0) return;

    const targetIndicesSet = new Set(targetIndices);
    const quantizedGps = quantizeGpsPairToNearestMeter(
      location.latitude,
      location.longitude,
    );

    // Store previous state for undo
    const previousData = files.map((f) => ({
      modifiedExifData: { ...f.modifiedExifData },
      hasChanges: f.hasChanges,
    }));

    // Apply location to selected files
    setFiles((prev) =>
      prev.map((f, i) => {
        if (targetIndicesSet.has(i)) {
          return {
            ...f,
            modifiedExifData: {
              ...f.modifiedExifData,
              latitude: quantizedGps.latitude,
              longitude: quantizedGps.longitude,
            },
            hasChanges: true,
          };
        }
        return f;
      }),
    );

    // Record for undo/redo
    recordAction(
      `Apply location "${location.name}" to ${targetIndices.length} files`,
      // Undo
      () => {
        setFiles((prev) =>
          prev.map((f, i) => ({
            ...f,
            modifiedExifData: previousData[i].modifiedExifData,
            hasChanges: previousData[i].hasChanges,
          })),
        );
      },
      // Redo
      () => {
        setFiles((prev) =>
          prev.map((f, i) => {
            if (targetIndicesSet.has(i)) {
              return {
                ...f,
                modifiedExifData: {
                  ...f.modifiedExifData,
                  latitude: quantizedGps.latitude,
                  longitude: quantizedGps.longitude,
                },
                hasChanges: true,
              };
            }
            return f;
          }),
        );
      },
      targetIndices,
    );
  };

  // Handle saving location from search results
  const handleSaveLocation = (location) => {
    const baseName = location.customName?.trim() || location.name || "";
    const shortName = baseName.split(',')[0].trim();
    const address = location.address || location.name || null;
    const result = addLocation(
      shortName,
      location.latitude,
      location.longitude,
      address
    );

    if (result.error) {
      toast.error(result.error);
      return false;
    }

    return true;
  };

  // Show upload screen when no files are loaded
  const showUploadScreen = files.length === 0 && !stripBase(location.pathname).startsWith("/settings");

  // Reset table row mapping whenever leaving table or data is cleared.
  useEffect(() => {
    if (viewMode !== "table" || files.length === 0) {
      setTableVisibleRowMap([]);
    }
  }, [viewMode, files.length, setTableVisibleRowMap]);

  return (
    <AppWithWorkspaceChrome
      showUploadScreen={showUploadScreen}
      isMobile={isMobile}
      viewMode={viewMode}
      calendarSelectedDate={calendarSelectedDate}
      setCalendarSelectedDate={setCalendarSelectedDate}
      setCalendarSelectedHour={setCalendarSelectedHour}
      setCalendarPanelExpanded={setCalendarPanelExpanded}
      editorMobileLeftOpen={editorMobileLeftOpen}
      setEditorMobileLeftOpen={setEditorMobileLeftOpen}
      editorMobileRightOpen={editorMobileRightOpen}
      setEditorMobileRightOpen={setEditorMobileRightOpen}
      calendarMobileLeftOpen={calendarMobileLeftOpen}
      setCalendarMobileLeftOpen={setCalendarMobileLeftOpen}
      calendarMobileRightOpen={calendarMobileRightOpen}
      setCalendarMobileRightOpen={setCalendarMobileRightOpen}
      handleApplyPreset={handleApplyPreset}
      handleApplyLocation={handleApplyLocation}
      handleSaveLocation={handleSaveLocation}
      presetModalOpen={presetModalOpen}
      setPresetModalOpen={setPresetModalOpen}
      locationModalOpen={locationModalOpen}
      setLocationModalOpen={setLocationModalOpen}
      keyboardShortcutsModalOpen={keyboardShortcutsModalOpen}
      setKeyboardShortcutsModalOpen={setKeyboardShortcutsModalOpen}
      keyboardShortcutsInitialView={keyboardShortcutsInitialView}
      dateTimeShortcutModalOpen={dateTimeShortcutModalOpen}
      setDateTimeShortcutModalOpen={setDateTimeShortcutModalOpen}
      dateTimeShortcutTargetIndices={dateTimeShortcutTargetIndices}
      handleApplyShortcutDateTime={handleApplyShortcutDateTime}
      selectedIndices={selectedIndices}
      selectedCellRange={selectedCellRange}
      files={files}
    />
  );
}

function AppShell({ initialFiles }) {
  const platform = usePlatform();
  const app = (
    <>
      <AppContent initialFiles={initialFiles} />
      <Toaster />
    </>
  );

  if (platform.name === "electron") {
    return <ProWall>{app}</ProWall>;
  }

  return app;
}

function App({
  router: RouterComponent = HashRouter,
  basePath = "",
  initialFiles = null,
  licenseAdapter = null,
}) {
  return (
    <RouterComponent>
      <BasePathProvider basePath={basePath}>
        <ThemeProvider>
          <LicenseProvider adapter={licenseAdapter}>
            <AppProvider>
              <FieldConfigProvider>
                <PresetsProvider>
                  <LocationsProvider>
                    <DemoProvider isDemo={initialFiles !== null} resetDemo={() => {}}>
                      <AppShell initialFiles={initialFiles} />
                    </DemoProvider>
                  </LocationsProvider>
                </PresetsProvider>
              </FieldConfigProvider>
            </AppProvider>
          </LicenseProvider>
        </ThemeProvider>
      </BasePathProvider>
    </RouterComponent>
  );
}

export default App;
